import React, { useRef } from 'react';
import { Link } from 'react-router';
import { useTracker } from '../../../../../tracker';
import { useImpression } from '../../../../../tracker/src/react-bindings';
import { Image } from '@rocco/components/image';

interface AnalyticsAmplifierTileProps {
    professionalId: number;
    image: string; // Main background image
    logo: string; // Logo image
    name: string;
    subtitle: string;
    website: string;
    backgroundColor: string;
}

export const AnalyticsAmplifierTile: React.FC<AnalyticsAmplifierTileProps> = ({
    professionalId,
    image,
    logo,
    name,
    subtitle,
    website,
    backgroundColor,
}) => {
    const ref = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();

    // Track impression when the card is visible
    useImpression({
        onImpression: async (url: URL) => {
            await tracker.log('AnalyticsAmplifierImpression', {
                url: url,
                targetTracker: ['archiproTracker', 'ga4Tracker'],
                data: {
                    professionalID: professionalId,
                    type: 'AnalyticsAmplifier',
                },
            });
        },
        ref,
    });

    const handleClick = () => {
        // Track click event
        void tracker.log('AnalyticsAmplifierClick', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                ExtraData: JSON.stringify({
                    professionalId: professionalId,
                }),
            },
            ga4Data: {
                professionalId: professionalId,
            },
        });
    };
    return (
        // Desktop type, logo and insets are sized in container units (`cqw`)
        // against the tile itself, so the layout holds at every browse column
        // count. Percentages are taken from the 4-column cell (438px at 1920,
        // where 1rem = 16px), so a 4-up tile renders exactly as before and the
        // narrower 5- and 6-up cells scale the whole composition down instead
        // of keeping viewport-sized type in a smaller box.
        //
        // Each ratio is capped by `min()` at the rem value it was derived from:
        // the 2- and 3-column cells are WIDER than the 4-up one, and unclamped
        // `cqw` would scale the mark and copy up past the size they have always
        // had (at 1920 a 2-up cell is ~916px, which would put the logo at 192px).
        // Because the root font-size is itself viewport-based, the rem cap and
        // the ratio stay equal at 4-up at every viewport, so the cap only bites
        // on the wider cells. Below `md` the grid is a fixed 2-up, so those
        // sizes stay in plain rem.
        <Link
            to={website}
            target="_blank"
            className={`group [container-type:inline-size] relative block h-full w-full overflow-hidden rounded-tile shadow-lg`}
            onClick={handleClick}
            ref={ref}
        >
            {/* Background Image */}
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${image})` }}
            />
            {/* Overlay */}
            <div
                className="absolute inset-0 top-0 left-0 h-full w-full"
                style={{
                    background:
                        'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 65.37%, rgba(0, 0, 0, 0.40) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.00) 63.04%, rgba(0, 0, 0, 0.40) 90.27%), rgba(0, 0, 0, 0.20)',
                }}
            />
            {/* Logo (top-left) */}
            <div className="absolute top-2 left-2.5 flex w-full items-center gap-2 md:top-[min(1rem,3.65cqw)] md:left-[min(1rem,3.65cqw)] md:w-[calc(100%-min(1.5rem,5.48cqw))] md:gap-[min(1rem,3.65cqw)]">
                <div
                    className="flex size-12.5 items-center justify-center overflow-hidden rounded-full p-0.5 md:size-[min(5.75rem,21cqw)] md:p-[min(0.125rem,0.46cqw)]"
                    style={{
                        backgroundColor: backgroundColor || 'white',
                    }}
                >
                    <Image
                        src={logo}
                        alt={`${name} logo`}
                        size="professionalLogo"
                    />
                </div>
                {/* Mobile */}
                <span className="w-24 font-display leading-[1.1] font-medium text-white md:w-[60%] md:max-w-[min(10.5rem,38.36cqw)] md:pr-[min(0.25rem,0.91cqw)] md:text-[min(1.25rem,4.57cqw)] md:font-medium">
                    {name}
                </span>
            </div>
            {/* Content (bottom) */}
            <div className="absolute right-3 bottom-4 left-3.5 z-10 md:right-[min(0.75rem,2.74cqw)] md:bottom-[min(2rem,7.3cqw)] md:left-[min(1.25rem,4.57cqw)]">
                <span className="flex w-42 max-w-full font-display text-3.5 leading-[1.1] font-medium text-white md:w-[min(15.5rem,56.6cqw)] md:text-[min(1.25rem,4.57cqw)] md:font-medium">
                    {subtitle}
                </span>
            </div>
        </Link>
    );
};
